import type { ReactNode } from "react";
import { cn } from "@/lib/cn";
import { LoadingState, EmptyState, ErrorState } from "../feedback/states";

export interface Column<Row> {
  key: string;
  header: ReactNode;
  /** cell renderer */
  cell: (row: Row) => ReactNode;
  align?: "left" | "right";
  className?: string;
}

export interface DataTableProps<Row> {
  columns: Column<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  onRowClick?: (row: Row) => void;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  empty?: { title: string; message?: string; action?: ReactNode };
}

export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  onRowClick,
  isLoading,
  isError,
  onRetry,
  empty,
}: DataTableProps<Row>) {
  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState onRetry={onRetry} />;
  if (rows.length === 0) {
    return <EmptyState title={empty?.title ?? "Aucune donnée"} message={empty?.message} action={empty?.action} />;
  }

  return (
    <div className="overflow-x-auto scroll-thin">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-border bg-bg3/45">
            {columns.map((col) => (
              <th
                key={col.key}
                className={cn(
                  "px-4 py-3 text-[10px] font-bold uppercase tracking-[0.1em] text-text3 whitespace-nowrap",
                  col.align === "right" ? "text-right" : "text-left",
                )}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onKeyDown={
                onRowClick
                  ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onRowClick(row);
                      }
                    }
                  : undefined
              }
              role={onRowClick ? "button" : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              className={cn(
                "border-b border-border last:border-0 transition-colors duration-150 focus-visible:outline-none focus-visible:bg-white/[0.04]",
                onRowClick && "cursor-pointer hover:bg-white/[0.035]",
              )}
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={cn(
                    "px-4 py-3.5 text-[12.5px] text-text2 align-middle",
                    col.align === "right" && "text-right",
                    col.className,
                  )}
                >
                  {col.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
